import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import {
  ArrowDown01Icon,
  AtIcon,
  Heading01Icon,
  Heading02Icon,
  Image01Icon,
  LeftToRightBlockQuoteIcon,
  LeftToRightListBulletIcon,
  LeftToRightListNumberIcon,
  Link02Icon,
  ParagraphIcon,
  Redo02Icon,
  Search01Icon,
  SolidLine01Icon,
  SourceCodeSquareIcon,
  TextAlignCenterIcon,
  TextAlignLeftIcon,
  TextAlignRightIcon,
  TextBoldIcon,
  TextColorIcon,
  TextItalicIcon,
  TextStrikethroughIcon,
  TextUnderlineIcon,
  Undo02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import Color from '@tiptap/extension-color';
import HardBreak from '@tiptap/extension-hard-break';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import { TextAlign } from '@tiptap/extension-text-align';
import { FontFamily, FontSize, LineHeight, TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import cn from 'classnames';
import { FilesFolders } from 'constants/files-folders-enum';
import { uploadSigned } from 'helpers/aws-s3';
import {
  mustachePlaceholdersToParamChipHtml,
  normalizeRichTextParamStorage,
  paramChipHtmlToMustachePlaceholders,
} from 'helpers/param-chip-markup';
import { useClickOutside } from 'hooks/click-outside';
import { useApiUploadActions } from 'hooks/upload-actions';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { SketchPicker } from 'react-color';
import * as mime from 'react-native-mime-types';
import ImageResize from 'tiptap-extension-resize-image';

import { FloatingInput } from 'components/FormComponents';

import { ParamChipExtension } from './ParamChipExtension';
import styles from './RichTextEditor.module.scss';

const FONT_SIZE_NUMBERS = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 44, 48];
const DEFAULT_FONT_SIZE_NUM = 14;
const LINE_HEIGHT_OPTIONS = [1, 1.25, 1.5, 1.75, 2, 2.25, 2.5, 2.75, 3];

/** Web-safe / system stacks only (no extra font files). See https://tiptap.dev/docs/editor/extensions/functionality/fontfamily */
const FONT_FAMILY_OPTIONS = [
  {
    value:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    label: 'System UI',
  },
  { value: 'Inter, sans-serif', label: 'Inter' },
  { value: 'Parkinsans, sans-serif', label: 'Parkinsans' },
  { value: 'Georgia, "Times New Roman", Times, serif', label: 'Georgia' },
  { value: '"Times New Roman", Times, serif', label: 'Times New Roman' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: '"Trebuchet MS", Helvetica, sans-serif', label: 'Trebuchet MS' },
  { value: '"Courier New", Courier, monospace', label: 'Courier New' },
  { value: 'Tahoma, Geneva, sans-serif', label: 'Tahoma' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Manrope, sans-serif', label: 'Manrope' },
];

const fontFamilyLabel = stored => {
  if (stored == null || stored === '') return 'Default';
  const found = FONT_FAMILY_OPTIONS.find(o => o.value === stored);
  return found ? found.label : 'Custom';
};

const pxToFontSizeNum = px => {
  if (typeof px !== 'string' || !px) return null;
  const n = parseInt(px.replace('px', '').trim(), 10);
  return Number.isFinite(n) ? n : null;
};

const parseLineHeightNum = stored => {
  if (typeof stored !== 'string' || !stored) return null;
  const n = parseFloat(stored);
  return Number.isFinite(n) ? n : null;
};

const FontSizeNumberLabel = ({ n, active }) => (
  <span
    className={cn(
      'text-lg font-semibold leading-none tabular-nums',
      active ? 'text-primary-6' : 'text-neutral-6',
    )}
    aria-hidden>
    {n}
  </span>
);

const CustomHardBreak = HardBreak.extend({
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.setHardBreak(),
    };
  },
});

const baseExtensions = [
  StarterKit.configure({ hardBreak: false }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Underline,
  Link.configure({ openOnClick: false }),
  TextStyle,
  Color,
  FontSize,
  FontFamily,
  LineHeight,
  CustomHardBreak,
  ImageResize,
  Image.configure({
    HTMLAttributes: {
      class: 'rich-text-image',
    },
  }),
  ParamChipExtension,
];

export const RichTextEditor = ({
  onChange = () => {},
  value,
  className,
  error,
  allowFileUpload = true,
  bucketFolder = FilesFolders.CompanyDocuments,
  textParams = {},
  storeTextParamsAsMustache = false,
  grow = false,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [color, setColor] = useState('#000000');
  const [variableMenuOpen, setVariableMenuOpen] = useState(false);
  const [variableSearch, setVariableSearch] = useState('');
  const [variableDropdownPosition, setVariableDropdownPosition] = useState({ top: 0, left: 0 });
  const [toolbarFontSize, setToolbarFontSize] = useState('');
  const [toolbarFontFamily, setToolbarFontFamily] = useState('');
  const [toolbarLineHeight, setToolbarLineHeight] = useState('');
  const [fontSizeMenuOpen, setFontSizeMenuOpen] = useState(false);
  const [fontFamilyMenuOpen, setFontFamilyMenuOpen] = useState(false);
  const [lineHeightMenuOpen, setLineHeightMenuOpen] = useState(false);
  const variableButtonRef = useRef(null);
  const variableDropdownRef = useRef(null);
  const fontSizeWrapperRef = useRef(null);
  const fontFamilyWrapperRef = useRef(null);
  const lineHeightWrapperRef = useRef(null);

  const hasTextParams = Object.keys(textParams).length > 0;

  const emitChange = useCallback(
    editorInstance => {
      const html = editorInstance.getHTML();
      onChange(storeTextParamsAsMustache ? paramChipHtmlToMustachePlaceholders(html) : html);
    },
    [onChange, storeTextParamsAsMustache],
  );

  const { getUpload, createUpload, signUpload } = useApiUploadActions();
  const editor = useEditor({
    extensions: baseExtensions,
    content: storeTextParamsAsMustache
      ? mustachePlaceholdersToParamChipHtml(value || '')
      : value || '',
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => {
      emitChange(editor);
    },
  });

  const updateVariableDropdownPosition = useCallback(() => {
    const el = variableButtonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setVariableDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left - 80,
    });
  }, []);

  const handleInsertVariable = useCallback(
    key => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .insertContent({ type: 'paramChip', attrs: { paramKey: key } })
        .run();
      emitChange(editor);
      setVariableMenuOpen(false);
      setVariableSearch('');
    },
    [editor, emitChange],
  );

  useEffect(() => {
    if (!editor) return;
    if (!storeTextParamsAsMustache) {
      if ((value || '') !== editor.getHTML()) {
        editor.commands.setContent(value || '');
      }
      return;
    }
    const current = normalizeRichTextParamStorage(editor.getHTML(), true);
    const incoming = normalizeRichTextParamStorage(value || '', true);
    if (current === incoming) return;
    editor.commands.setContent(mustachePlaceholdersToParamChipHtml(value || ''));
  }, [value, editor, storeTextParamsAsMustache]);

  useEffect(() => {
    if (!editor) return;
    const syncTextStyleToolbar = () => {
      const attrs = editor.getAttributes('textStyle');
      setToolbarFontSize(typeof attrs.fontSize === 'string' ? attrs.fontSize : '');
      setToolbarFontFamily(typeof attrs.fontFamily === 'string' ? attrs.fontFamily : '');
      setToolbarLineHeight(typeof attrs.lineHeight === 'string' ? attrs.lineHeight : '');
    };
    syncTextStyleToolbar();
    editor.on('selectionUpdate', syncTextStyleToolbar);
    editor.on('transaction', syncTextStyleToolbar);
    return () => {
      editor.off('selectionUpdate', syncTextStyleToolbar);
      editor.off('transaction', syncTextStyleToolbar);
    };
  }, [editor]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (variableButtonRef.current?.contains(e.target)) return;
      if (variableDropdownRef.current?.contains(e.target)) return;
      setVariableMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === '@') {
        e.preventDefault();
        setVariableSearch('');
        setVariableMenuOpen(true);
        updateVariableDropdownPosition();
      }
    },
    [updateVariableDropdownPosition],
  );

  useEffect(() => {
    if (!variableMenuOpen) return;
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setVariableMenuOpen(false);
        setVariableSearch('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [variableMenuOpen]);

  useEffect(() => {
    if (!variableMenuOpen) return;
    updateVariableDropdownPosition();
    window.addEventListener('scroll', updateVariableDropdownPosition, true);
    window.addEventListener('resize', updateVariableDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateVariableDropdownPosition, true);
      window.removeEventListener('resize', updateVariableDropdownPosition);
    };
  }, [variableMenuOpen, updateVariableDropdownPosition]);

  useEffect(() => {
    if (!variableMenuOpen) return;
    const handleEscape = e => {
      if (e.key === 'Escape') {
        setVariableMenuOpen(false);
        setVariableSearch('');
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [variableMenuOpen]);

  const [showFloatingInput, setShowFloatingInput] = useState(false);
  const [currentHref, setCurrentHref] = useState('');

  const paramKeys = Object.keys(textParams);
  const filteredParamKeys = variableSearch
    ? paramKeys.filter(k => k.toLowerCase().includes(variableSearch.toLowerCase()))
    : paramKeys;

  const variableDropdownContent = variableMenuOpen && hasTextParams && (
    <div
      ref={variableDropdownRef}
      data-click-outside-ignore
      className="fixed bg-white border border-neutral-4 rounded-2xl p-2 flex flex-col gap-1.5 shadow-xl w-[min(40rem,calc(100vw-2rem))] z-[9999]"
      style={{
        top: variableDropdownPosition.top,
        left: variableDropdownPosition.left,
      }}>
      <div className="flex items-center gap-1.5 bg-neutral-2 border border-neutral-4 rounded-full px-2 py-2">
        <HugeiconsIcon icon={Search01Icon} size="16" strokeWidth="2" className="text-neutral-6" />
        <input
          type="text"
          value={variableSearch}
          onChange={e => setVariableSearch(e.target.value)}
          placeholder="Search for a variable..."
          className="bg-transparent outline-none text-xl text-neutral-6 placeholder:text-neutral-6 w-full"
        />
      </div>
      {filteredParamKeys.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {filteredParamKeys.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => handleInsertVariable(key)}
              className="flex items-center gap-1 px-2 py-1 bg-primary-4/15 border border-primary-5 rounded-2xl text-xl font-regular text-primary-5 hover:bg-primary-1 hover:border-primary-3 hover:text-primary-5 transition-colors whitespace-nowrap w-max">
              <HugeiconsIcon icon={AtIcon} size="16" strokeWidth="2" className="text-primary-5" />
              {key}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-5 text-center py-1">No variables found</p>
      )}
    </div>
  );

  const editorWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  useClickOutside(editorWrapperRef, () => setShowPicker(false));
  useClickOutside(fontSizeWrapperRef, () => setFontSizeMenuOpen(false));
  useClickOutside(fontFamilyWrapperRef, () => setFontFamilyMenuOpen(false));
  useClickOutside(lineHeightWrapperRef, () => setLineHeightMenuOpen(false));

  const setLink = useCallback(
    v => {
      if (v) {
        editor.chain().focus().setLink({ href: v }).run();
      } else {
        editor.chain().focus().unsetLink().run(); // Optional: remove link if empty
      }
      setShowFloatingInput(false);
      setCurrentHref(''); // Reset
    },
    [editor],
  );

  const openLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href || '';
    setCurrentHref(previousUrl); // Save current href
    setShowFloatingInput(true);
  }, [editor]);

  const handleColorChange = useCallback(
    color => {
      setColor(color.hex);
      editor.chain().focus().setColor(color.hex).run();
    },
    [editor],
  );

  const handleImageUpload = useCallback(
    async file => {
      try {
        const signedFile = await signUpload({
          file_name: file.name,
          file_type: file.type,
          file_ext:
            mime.extension(file.type) ||
            (file.name && file.name.includes('.') ? file.name.split('.').pop() : null) ||
            'bin',
          bucket_folder: bucketFolder,
        });

        // Upload to S3
        await uploadSigned(signedFile.url, file, file.type);

        const upload = await createUpload({
          file_name: signedFile.file_name,
          file_type: signedFile.file_type,
          hash_key: signedFile.key,
          metadata: {
            rich_text_editor: true,
          },
          modelParams: { rich_text_editor: true },
        });

        const { url } = await getUpload(upload?.id);

        editor.chain().focus().setImage({ src: url }).run();
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    },
    [editor, createUpload, signUpload],
  );

  const MENU_ITEMS = useMemo(() => {
    let items = [
      {
        label: 'Undo',
        onClick: editor => editor.chain().focus().undo().run(),
        icon: Undo02Icon,
        isActive: () => false,
        size: 16,
      },
      {
        label: 'Redo',
        onClick: editor => editor.chain().focus().redo().run(),
        icon: Redo02Icon,
        isActive: () => false,
        size: 16,
      },
      {
        label: 'Bold',
        onClick: editor => editor.chain().focus().toggleBold().run(),
        icon: TextBoldIcon,
        isActive: editor => editor.isActive('bold'),
        size: 16,
      },
      {
        label: 'Italic',
        onClick: editor => editor.chain().focus().toggleItalic().run(),
        icon: TextItalicIcon,
        isActive: editor => editor.isActive('italic'),
        size: 16,
      },
      {
        label: 'Underline',
        onClick: editor => editor.chain().focus().toggleUnderline().run(),
        icon: TextUnderlineIcon,
        isActive: editor => editor.isActive('underline'),
        size: 16,
      },
      {
        label: 'Strike',
        onClick: editor => editor.chain().focus().toggleStrike().run(),
        icon: TextStrikethroughIcon,
        isActive: editor => editor.isActive('strike'),
        size: 16,
      },
      {
        label: 'Bullet List',
        onClick: editor => editor.chain().focus().toggleBulletList().run(),
        icon: LeftToRightListBulletIcon,
        isActive: editor => editor.isActive('bulletList'),
        size: 18,
      },
      {
        label: 'Ordered List',
        onClick: editor => editor.chain().focus().toggleOrderedList().run(),
        icon: LeftToRightListNumberIcon,
        isActive: editor => editor.isActive('orderedList'),
        size: 18,
      },
      {
        label: 'H1',
        onClick: editor => editor.chain().focus().toggleHeading({ level: 1 }).run(),
        icon: Heading01Icon,
        isActive: editor => editor.isActive('heading', { level: 1 }),
        size: 18,
      },
      {
        label: 'H2',
        onClick: editor => editor.chain().focus().toggleHeading({ level: 2 }).run(),
        icon: Heading02Icon,
        isActive: editor => editor.isActive('heading', { level: 2 }),
        size: 18,
      },
      {
        label: 'Paragraph',
        onClick: editor => editor.chain().focus().setParagraph().run(),
        icon: ParagraphIcon,
        isActive: editor => editor.isActive('paragraph'),
        size: 16,
      },
      {
        label: 'Text Color',
        onClick: () => setShowPicker(v => !v),
        icon: TextColorIcon,
        isActive: editor => editor.isActive({ textStyle: 'color' }),
        size: 16,
      },
      {
        label: 'Align Left',
        onClick: editor => editor.chain().focus().setTextAlign('left').run(),
        icon: TextAlignLeftIcon,
        isActive: editor => editor.isActive({ textAlign: 'left' }),
        size: 16,
      },
      {
        label: 'Align Center',
        onClick: editor => editor.chain().focus().setTextAlign('center').run(),
        icon: TextAlignCenterIcon,
        isActive: editor => editor.isActive({ textAlign: 'center' }),
        size: 16,
      },
      {
        label: 'Align Right',
        onClick: editor => editor.chain().focus().setTextAlign('right').run(),
        icon: TextAlignRightIcon,
        isActive: editor => editor.isActive({ textAlign: 'right' }),
        size: 16,
      },
      {
        label: 'Set Link',
        onClick: openLink,
        icon: Link02Icon,
        isActive: editor => editor.isActive('link'),
        size: 18,
      },
      {
        label: 'Code Block',
        onClick: editor => editor.chain().focus().toggleCodeBlock().run(),
        icon: SourceCodeSquareIcon,
        isActive: editor => editor.isActive('codeBlock'),
        size: 18,
      },
      {
        label: 'Blockquote',
        onClick: editor => editor.chain().focus().toggleBlockquote().run(),
        icon: LeftToRightBlockQuoteIcon,
        isActive: editor => editor.isActive('blockquote'),
        size: 18,
      },
      {
        label: 'Horizontal Rule',
        onClick: editor => editor.chain().focus().setHorizontalRule().run(),
        icon: SolidLine01Icon,
        isActive: editor => editor.isActive('horizontalRule'),
        size: 18,
      },
    ];

    if (allowFileUpload) {
      items.push({
        label: 'Image',
        onClick: () => fileInputRef.current?.click(),
        icon: Image01Icon,
        isActive: () => false,
        size: 18,
      });
    }

    return items;
  }, [editor, allowFileUpload]);

  const toolbarAfterTextColorIndex = useMemo(
    () => MENU_ITEMS.findIndex(item => item.label === 'Align Left'),
    [MENU_ITEMS],
  );

  const handleFontSizePick = useCallback(
    n => {
      if (!editor) return;
      editor.chain().focus().setFontSize(`${n}px`).run();
      setFontSizeMenuOpen(false);
    },
    [editor],
  );

  const handleFontFamilyPick = useCallback(
    value => {
      if (!editor) return;
      if (value === '' || value == null) {
        editor.chain().focus().unsetFontFamily().run();
      } else {
        editor.chain().focus().setFontFamily(value).run();
      }
      setFontFamilyMenuOpen(false);
    },
    [editor],
  );

  const handleLineHeightPick = useCallback(
    value => {
      if (!editor) return;
      if (value === '' || value == null) {
        editor.chain().focus().unsetLineHeight().run();
      } else {
        editor.chain().focus().setLineHeight(String(value)).run();
      }
      setLineHeightMenuOpen(false);
    },
    [editor],
  );

  useEffect(() => {
    if (!fontSizeMenuOpen && !fontFamilyMenuOpen && !lineHeightMenuOpen) return;
    const onKey = e => {
      if (e.key === 'Escape') {
        setFontSizeMenuOpen(false);
        setFontFamilyMenuOpen(false);
        setLineHeightMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [fontSizeMenuOpen, fontFamilyMenuOpen, lineHeightMenuOpen]);

  useEffect(() => {
    if (!showPicker) return;
    const onKey = e => {
      if (e.key === 'Escape') {
        setShowPicker(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showPicker]);

  const explicitFontSizeNum = editor ? pxToFontSizeNum(toolbarFontSize) : null;
  const displayFontSizeNum = explicitFontSizeNum ?? DEFAULT_FONT_SIZE_NUM;
  const hasExplicitFontFamily = Boolean(editor && toolbarFontFamily);
  const explicitLineHeightNum = editor ? parseLineHeightNum(toolbarLineHeight) : null;
  const hasExplicitLineHeight = explicitLineHeightNum != null;

  if (!editor) return null;

  const renderToolbarItem = (item, index) => (
    <div key={`${item.label}-${index}`} className={item.label === 'Text Color' ? 'u-relative' : ''}>
      <OverlayTrigger
        trigger={item.label === undefined ? 'none' : ['hover', 'focus', 'click']}
        placement="top"
        overlay={<Tooltip>{item.label}</Tooltip>}>
        <HugeiconsIcon
          size={item.size}
          icon={item.icon}
          color={
            item.label === 'Text Color' ? color : item.isActive(editor) ? '#390a98' : '#2d264d'
          }
          strokeWidth={item.label === 'Bold' || item.isActive(editor) ? 2.5 : 1.5}
          className={cn(styles.iconButton, 'u-pointer')}
          onClick={() => item.onClick(editor)}
        />
      </OverlayTrigger>
      {item.label === 'Text Color' && showPicker && (
        <div ref={editorWrapperRef} className={cn(styles.colorPicker, 'u-z-index-10')}>
          <SketchPicker color={color} onChangeComplete={handleColorChange} />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={cn(
        styles.root,
        'd-flex flex-column u-border u-border-radius surface-secondary',
        className,
        { [styles.error]: error?.message, [styles.rootGrow]: grow },
      )}>
      {allowFileUpload && (
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept="image/*"
          onChange={e => {
            const file = e.target.files?.[0];
            if (file) {
              handleImageUpload(file);
            }
            e.target.value = ''; // Reset input
          }}
        />
      )}
      {/* Toolbar */}
      <div className={cn(styles.toolbar, 'd-flex u-gap-medium align-items-center flex-wrap')}>
        {(toolbarAfterTextColorIndex >= 0
          ? MENU_ITEMS.slice(0, toolbarAfterTextColorIndex)
          : MENU_ITEMS
        ).map(renderToolbarItem)}
        {toolbarAfterTextColorIndex >= 0 && (
          <div ref={fontSizeWrapperRef} className="relative flex items-center">
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="top"
              overlay={<Tooltip>Font size</Tooltip>}>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-left shadow-sm transition-colors',
                  'border-neutral-4 bg-neutral-3 hover:bg-neutral-2',
                  explicitFontSizeNum != null ? 'border-primary-4/40 bg-primary-1/40' : null,
                )}
                aria-expanded={fontSizeMenuOpen}
                aria-haspopup="listbox"
                onClick={() => setFontSizeMenuOpen(o => !o)}>
                <FontSizeNumberLabel n={displayFontSizeNum} active={explicitFontSizeNum != null} />
                <HugeiconsIcon
                  icon={ArrowDown01Icon}
                  size={14}
                  strokeWidth={2}
                  className={cn(
                    'shrink-0',
                    explicitFontSizeNum != null ? 'text-primary-6' : 'text-neutral-6',
                  )}
                />
              </button>
            </OverlayTrigger>
            {fontSizeMenuOpen && (
              <ul
                className="absolute left-0 top-[calc(100%+6px)] z-[200] min-w-[9rem] list-none rounded-2xl border border-neutral-4 bg-white py-1.5 shadow-lg"
                role="listbox">
                {FONT_SIZE_NUMBERS.map(n => {
                  const selected = explicitFontSizeNum === n;
                  return (
                    <li key={n} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={cn(
                          'flex w-full items-center px-3 py-2 text-left transition-colors',
                          selected
                            ? 'bg-neutral-3 text-primary-6'
                            : 'text-neutral-7 hover:bg-neutral-2',
                        )}
                        onClick={() => handleFontSizePick(n)}>
                        <FontSizeNumberLabel n={n} active={selected} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {toolbarAfterTextColorIndex >= 0 && (
          <div ref={fontFamilyWrapperRef} className="relative flex items-center">
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="top"
              overlay={<Tooltip>Font family</Tooltip>}>
              <button
                type="button"
                className={cn(
                  'inline-flex max-w-[10rem] items-center gap-1 rounded-full border px-3 py-1.5 text-left text-sm shadow-sm transition-colors',
                  'border-neutral-4 bg-neutral-3 hover:bg-neutral-2',
                  hasExplicitFontFamily ? 'border-primary-4/40 bg-primary-1/40' : null,
                )}
                aria-expanded={fontFamilyMenuOpen}
                aria-haspopup="listbox"
                onClick={() => setFontFamilyMenuOpen(o => !o)}>
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate font-medium text-lg',
                    hasExplicitFontFamily ? 'text-primary-6' : 'text-neutral-6',
                  )}>
                  {fontFamilyLabel(toolbarFontFamily)}
                </span>
                <HugeiconsIcon
                  icon={ArrowDown01Icon}
                  size={14}
                  strokeWidth={2}
                  className={cn(
                    'shrink-0',
                    hasExplicitFontFamily ? 'text-primary-6' : 'text-neutral-6',
                  )}
                />
              </button>
            </OverlayTrigger>
            {fontFamilyMenuOpen && (
              <ul
                className="absolute left-0 top-[calc(100%+6px)] z-[200] max-h-[min(20rem,70vh)] min-w-[12rem] list-none overflow-y-auto rounded-2xl border border-neutral-4 bg-white py-1.5 shadow-lg"
                role="listbox">
                <li role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={!toolbarFontFamily}
                    className={cn(
                      'flex w-full items-center px-3 py-2 text-left text-lg transition-colors',
                      !toolbarFontFamily
                        ? 'bg-neutral-3 text-primary-6'
                        : 'text-neutral-7 hover:bg-neutral-2',
                    )}
                    style={{ fontFamily: 'inherit' }}
                    onClick={() => handleFontFamilyPick('')}>
                    Unset
                  </button>
                </li>
                {FONT_FAMILY_OPTIONS.map(option => {
                  const selected = toolbarFontFamily === option.value;
                  return (
                    <li key={option.label} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={cn(
                          'flex w-full items-center px-3 py-2 text-left text-lg transition-colors',
                          selected
                            ? 'bg-neutral-3 text-primary-6'
                            : 'text-neutral-7 hover:bg-neutral-2',
                        )}
                        style={{ fontFamily: option.value }}
                        onClick={() => handleFontFamilyPick(option.value)}>
                        {option.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {toolbarAfterTextColorIndex >= 0 && (
          <div ref={lineHeightWrapperRef} className="relative flex items-center">
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement="top"
              overlay={<Tooltip>Line height</Tooltip>}>
              <button
                type="button"
                className={cn(
                  'inline-flex min-w-[5.5rem] items-center gap-1 rounded-full border px-3 py-1.5 text-left text-sm shadow-sm transition-colors',
                  'border-neutral-4 bg-neutral-3 hover:bg-neutral-2',
                  hasExplicitLineHeight ? 'border-primary-4/40 bg-primary-1/40' : null,
                )}
                aria-expanded={lineHeightMenuOpen}
                aria-haspopup="listbox"
                onClick={() => setLineHeightMenuOpen(o => !o)}>
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate font-medium text-lg tabular-nums',
                    hasExplicitLineHeight ? 'text-primary-6' : 'text-neutral-6',
                  )}>
                  {hasExplicitLineHeight ? String(explicitLineHeightNum) : 'Default'}
                </span>
                <HugeiconsIcon
                  icon={ArrowDown01Icon}
                  size={14}
                  strokeWidth={2}
                  className={cn(
                    'shrink-0',
                    hasExplicitLineHeight ? 'text-primary-6' : 'text-neutral-6',
                  )}
                />
              </button>
            </OverlayTrigger>
            {lineHeightMenuOpen && (
              <ul
                className="absolute left-0 top-[calc(100%+6px)] z-[200] min-w-[9rem] list-none rounded-2xl border border-neutral-4 bg-white py-1.5 shadow-lg"
                role="listbox">
                <li role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={!hasExplicitLineHeight}
                    className={cn(
                      'flex w-full items-center px-3 py-2 text-left text-lg transition-colors',
                      !hasExplicitLineHeight
                        ? 'bg-neutral-3 text-primary-6'
                        : 'text-neutral-7 hover:bg-neutral-2',
                    )}
                    onClick={() => handleLineHeightPick('')}>
                    Default
                  </button>
                </li>
                {LINE_HEIGHT_OPTIONS.map(n => {
                  const selected =
                    hasExplicitLineHeight && Math.abs(explicitLineHeightNum - n) < 0.001;
                  return (
                    <li key={n} role="none">
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={cn(
                          'flex w-full items-center px-3 py-2 text-left text-lg tabular-nums transition-colors',
                          selected
                            ? 'bg-neutral-3 text-primary-6'
                            : 'text-neutral-7 hover:bg-neutral-2',
                        )}
                        onClick={() => handleLineHeightPick(n)}>
                        {String(n)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        {toolbarAfterTextColorIndex >= 0 &&
          MENU_ITEMS.slice(toolbarAfterTextColorIndex).map(renderToolbarItem)}
        {hasTextParams && (
          <div ref={variableButtonRef} className="d-flex align-items-center u-gap-small">
            <OverlayTrigger
              trigger={['hover', 'focus', 'click']}
              placement="top"
              overlay={<Tooltip>Insert variable</Tooltip>}>
              <span
                role="button"
                tabIndex={0}
                className={cn(styles.iconButton, 'u-pointer d-flex align-items-center u-gap-small')}
                onClick={() => {
                  updateVariableDropdownPosition();
                  setVariableMenuOpen(v => !v);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    updateVariableDropdownPosition();
                    setVariableMenuOpen(v => !v);
                  }
                }}>
                <HugeiconsIcon
                  size={18}
                  icon={AtIcon}
                  color="#390a98"
                  strokeWidth={2}
                  className="text-primary-5"
                />
              </span>
            </OverlayTrigger>
          </div>
        )}
      </div>
      <div className={cn('u-border-bottom')} />
      {/* Editor Area */}
      <div className={cn(styles.contentWrapper, { [styles.contentWrapperGrow]: grow })}>
        {showFloatingInput && (
          <FloatingInput
            onClick={v => setLink(v)}
            defaultValue={currentHref}
            onClose={() => setShowFloatingInput(false)}
          />
        )}
        <EditorContent
          editor={editor}
          role="presentation"
          onKeyDown={handleKeyDown}
          className={cn(
            styles.simpleEditorContent,
            { [styles.simpleEditorContentGrow]: grow },
            'u-padding primary-text u-text-cursor',
          )}
        />
      </div>
      {hasTextParams && (
        <div className="flex mt-2">
          <span className="text-xl text-text-tertiary text-center py-1 px-6 flex items-center flex-wrap gap-1 w-full">
            Click{' '}
            <HugeiconsIcon icon={AtIcon} size="16" strokeWidth="2" className="text-primary-5" /> in
            the toolbar or type{' '}
            <span className="bg-neutral-2 rounded-xl px-1 py-0.5 text-neutral-6 border border-neutral-4">
              @
            </span>{' '}
            to insert a variable
          </span>
        </div>
      )}
      {variableDropdownContent && createPortal(variableDropdownContent, document.body)}
    </div>
  );
};
